import { useEffect, useState, useRef, useCallback } from "react";
import styles from "./StoreProducts.module.scss";
import { ProductsTypes } from "../../../../api/types/products.types";
import ProductsService from "../../../../api/services/products.service";
import { useParams } from "react-router-dom";
import Loading from "../../../../components/common/loading/Loading";
import ProductCard from "../../../../components/common/product-card/ProductCard";
import Button from "../../../../components/common/button/Button";
import { useTranslation } from "react-i18next";
import TitleUpdater from "../../../../components/common/title/TitleUpdater";
import { useLanguage } from "../../../../context/LanguageContext";
import { groupProductVariants } from "../../../../utils/groupProductVariants";
import Pagination from "../../../../components/common/pagination/Pagination";
import emptystoreprod from "../../../../assets/images/store/emptystoreprod.png";

const PRODUCTS_PER_PAGE = 50;
const INFINITE_SCROLL_LIMIT = 300;

const StoreProducts = () => {
  const [products, setProducts] = useState<ProductsTypes[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [useInfiniteScroll, setUseInfiniteScroll] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  const { id } = useParams();
  const { t } = useTranslation("translation");
  const { currentLanguage } = useLanguage();
  const observerTarget = useRef<HTMLDivElement>(null);

  const fetchProducts = useCallback(
    async (pageNum: number, isInitial: boolean = false) => {
      if (!id) return;

      isInitial ? setLoading(true) : setLoadingMore(true);

      try {
        const res = await ProductsService.getProductsStore(
          Number(id),
          pageNum,
          PRODUCTS_PER_PAGE,
        );
        const variants = res?.data?.data || [];
        const count = res?.data?.count || 0;
        setTotalCount(count);

        const groupedProducts = groupProductVariants(variants);

        setProducts((prev) =>
          isInitial ? groupedProducts : [...prev, ...groupedProducts],
        );
        const itemsLoaded = pageNum * PRODUCTS_PER_PAGE;
        const noNewGroupedProducts = groupedProducts.length === 0;
        setHasMore(!noNewGroupedProducts && itemsLoaded < count);
        if (isInitial) {
          setPage(pageNum);
        }
      } catch (error) {
        console.error("Fetch error:", error);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [id],
  );

  useEffect(() => {
    setUseInfiniteScroll(true);
    fetchProducts(1, true);
  }, [id, fetchProducts]);

  useEffect(() => {
    if (products.length >= INFINITE_SCROLL_LIMIT) {
      setUseInfiniteScroll(false);
    }
  }, [products.length]);

  useEffect(() => {
    if (!useInfiniteScroll || !hasMore || loadingMore || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          const nextPage = page + 1;
          setPage(nextPage);
          fetchProducts(nextPage);
        }
      },
      { threshold: 0.5 },
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) observer.observe(currentTarget);

    return () => observer.disconnect();
  }, [useInfiniteScroll, hasMore, loadingMore, loading, page, fetchProducts]);

  const getLocalizedName = (product: ProductsTypes) => {
    const names: Record<string, string> = {
      az: product.name_az,
      de: product.name_de,
      es: product.name_es,
    };
    return names[currentLanguage] || product.name_en;
  };

  if (loading) return <Loading />;

  return (
    <div className={styles.products}>
      <TitleUpdater title={t("products")} />
      <div className="container">
        {products.length === 0 ? (
          <div className={styles.emptyBasket}>
            <img src={emptystoreprod} alt="Empty store products" />
            <br />
            <h3>{t("no_products")}</h3>
            <p>{t("no_products_description")}</p>
          </div>
        ) : (
          <>
            <div className={styles.products_list}>
              {products.map((p, i) => (
                <ProductCard
                  id={p.id}
                  key={`${p.id}-${i}`}
                  title={getLocalizedName(p)}
                  currency={p.currency_name}
                  price={p.price?.toString()}
                  discount_percent={p.discount_percent?.toString()}
                  discount_price={p.discount_price?.toString()}
                  image={p.images?.[0]?.image || ""}
                  stock_quantity={p.stock_quantity}
                  wishlist={p.is_wish}
                  images={p.images}
                  attributes={p.attributes}
                />
              ))}
            </div>

            {useInfiniteScroll && hasMore && (
              <div ref={observerTarget} className={styles.scroll_trigger}>
                {loadingMore && <Loading />}
              </div>
            )}

            {!useInfiniteScroll && hasMore && (
              <div className={styles.load_more_container}>
                <Button
                  onClick={() => {
                    const nextPage = page + 1;
                    setPage(nextPage);
                    fetchProducts(nextPage);
                  }}
                  loading={loadingMore}
                  disabled={loadingMore}
                  className={styles.load_more_btn}
                >
                  {t("load_more")}
                </Button>
              </div>
            )}

            <Pagination
              currentPage={page}
              totalPages={Math.ceil(totalCount / PRODUCTS_PER_PAGE)}
              onPageChange={(newPage) => {
                setPage(newPage);
                fetchProducts(newPage, true);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              showLoadMore={!useInfiniteScroll && hasMore}
              onLoadMore={() => {
                const nextPage = page + 1;
                setPage(nextPage);
                fetchProducts(nextPage);
              }}
              loadMoreText={t("load_more") || ""}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default StoreProducts;
